/**
 * Canonical brand-kit DOM applier — shared between customer-app and admin-app.
 *
 * Responsibility:
 *   Given a tenant's `ClientConfig.brand`, mutate `document.documentElement`
 *   so every CSS rule and Tailwind utility renders in the tenant's palette.
 *
 *   - Sets ~17 CSS custom properties on `:root`:
 *     - 7 base `--brand-*` tokens consumed by hand-written CSS
 *       (index.css `:root` defaults + page-level `var(--brand-primary, ...)`).
 *     - 10 `--color-brand-*` Tailwind v4 theme tokens consumed by utility
 *       classes (`bg-brand-primary`, `text-brand-cream`, `border-brand-sand`).
 *       These override the hardcoded ALA copper baked into the `@theme`
 *       block in `index.css` — inline-style specificity wins over the
 *       `@theme` declaration so the tenant value is what paints.
 *   - Injects a single Google Fonts <link> for the brand fonts (heading +
 *     body), deduped by element id so StrictMode double-invocation doesn't
 *     stack duplicates.
 *   - Updates `document.title` with the brand name + optional suffix
 *     (admin-app passes ` · Admin`).
 *
 * Why this lives in @ferro/core (and not duplicated per app):
 *   Pre-2026-05-12 the customer-app and admin-app each owned their own
 *   `installBrandStyles`. Tailwind theme tokens were only overridden in the
 *   customer-app's copy because that's where the Tailwind utilities live,
 *   and the admin-app's copy was missing the override entirely — meaning
 *   the moment admin-app picked up any `bg-brand-*` class, ALA copper would
 *   bleed onto HTHD's admin surface. Consolidating the helper into a
 *   shared package means there's exactly one source of truth for the
 *   token mapping; both apps thread the same kit through it.
 *
 * What is intentionally NOT tenant-mapped:
 *   Semantic-neutral tokens (`--color-brand-sand`, `--color-brand-success`,
 *   `--color-brand-warning`, `--color-brand-error`) keep their platform
 *   defaults from `index.css`'s `@theme` block. These represent product-
 *   wide semantic meaning (gray dividers, success-green, warning-yellow,
 *   error-red) and should not pick up tenant brand color. If a tenant
 *   needs to override semantic neutrals later, expose them through the
 *   `brandSchema` first.
 *
 * Idempotent — calling twice with the same kit is a no-op (same end state).
 */
import type { ClientConfig } from '@ferro/config';
import { accessibleLinkColor } from './contrast.js';

export interface ApplyBrandKitOptions {
  /**
   * Suffix appended to `document.title`. Empty string by default for the
   * customer-app; admin-app passes ` · Admin` so the staff-facing tab
   * reads e.g. `Happy Tail Happy Dog · Admin`.
   */
  titleSuffix?: string;
}

/**
 * Apply a tenant brand kit to the document.
 *
 * @param config  Tenant client config — `brand` field is what's consumed.
 *                The rest of the config is ignored here but the full type
 *                is accepted so callers can hand in the same object they
 *                already have without destructuring.
 * @param options Optional knobs (currently just title suffix).
 */
export function applyBrandKit(
  config: ClientConfig,
  options: ApplyBrandKitOptions = {},
): void {
  const { brand } = config;
  const root = document.documentElement;

  // ---- 1. Base `--brand-*` runtime tokens ----
  // These are read by hand-written CSS in each app's `index.css`. Order
  // matters: the derived `--color-brand-primary-light` / `*-dark` /
  // `*-forest-muted` tokens below use `var(--brand-primary)` /
  // `var(--brand-secondary)` in their `color-mix(...)` expressions, so
  // the base tokens MUST be set before the derived ones.
  root.style.setProperty('--brand-primary', brand.colors.primary);
  root.style.setProperty('--brand-secondary', brand.colors.secondary);
  root.style.setProperty('--brand-accent', brand.colors.accent);
  if (brand.colors.text) {
    root.style.setProperty('--brand-text', brand.colors.text);
  }
  if (brand.colors.background) {
    root.style.setProperty('--brand-background', brand.colors.background);
  }
  // Ted 10-09 — `--brand-link`: the tenant's primary, darkened just enough
  // to read as text (≥4.5:1 on white, the card surface and the page). See
  // contrast.ts. Removed rather than left stale when the kit has no
  // parseable primary, so the stylesheet's fallback applies.
  const link = accessibleLinkColor({
    primary: brand.colors.primary,
    secondary: brand.colors.secondary,
    background: brand.colors.background ?? null,
  });
  if (link) root.style.setProperty('--brand-link', link);
  else root.style.removeProperty('--brand-link');
  root.style.setProperty(
    '--brand-font-heading',
    `"${brand.fonts.heading}", serif`,
  );
  root.style.setProperty(
    '--brand-font-body',
    `"${brand.fonts.body}", sans-serif`,
  );

  // ---- 2. Tailwind v4 `--color-brand-*` theme tokens ----
  // `index.css` declares `@theme { --color-brand-primary: #b87333; ... }`
  // baking in ALA copper. Pages using Tailwind utilities like
  // `bg-brand-cream`, `text-brand-primary`, `bg-brand-primary` compile
  // to `background-color: var(--color-brand-cream)` etc. Without these
  // runtime overrides every tenant rendered ALA palette regardless of
  // brand. Inline-style on `:root` has higher specificity than the
  // `@theme` declaration → tenant value wins.
  //
  // Tenant-mapped: primary, primary-light, primary-dark, secondary,
  // accent, cream, warm-white, amber, forest, forest-muted.
  // Platform defaults preserved: sand (semantic neutral), success,
  // warning, error (semantic — see file header).
  root.style.setProperty('--color-brand-primary', brand.colors.primary);
  root.style.setProperty(
    '--color-brand-primary-light',
    'color-mix(in srgb, var(--brand-primary) 70%, white)',
  );
  root.style.setProperty(
    '--color-brand-primary-dark',
    'color-mix(in srgb, var(--brand-primary) 70%, black)',
  );
  root.style.setProperty('--color-brand-secondary', brand.colors.secondary);
  root.style.setProperty('--color-brand-accent', brand.colors.accent);
  const warmCream = brand.colors.background ?? '#fdf8f3';
  const warmWhite = brand.colors.background ?? '#fffaf3';
  root.style.setProperty('--color-brand-cream', warmCream);
  root.style.setProperty('--color-brand-warm-white', warmWhite);
  root.style.setProperty('--color-brand-amber', brand.colors.accent);
  root.style.setProperty('--color-brand-forest', brand.colors.secondary);
  root.style.setProperty(
    '--color-brand-forest-muted',
    'color-mix(in srgb, var(--brand-secondary) 60%, white)',
  );

  // ---- 3. Google Fonts <link> ----
  // Single deduped link element; updated href in place when fonts change.
  const fontFamilies = [brand.fonts.heading, brand.fonts.body]
    .map((f) => `${f.replace(/\s+/g, '+')}:wght@400;500;600;700`)
    .map((spec) => `family=${spec}`)
    .join('&');
  const href = `https://fonts.googleapis.com/css2?${fontFamilies}&display=swap`;

  const existing = document.getElementById(
    'ferro-brand-fonts',
  ) as HTMLLinkElement | null;
  if (existing) {
    if (existing.href !== href) {
      existing.href = href;
    }
  } else {
    const link = document.createElement('link');
    link.id = 'ferro-brand-fonts';
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  // ---- 4. Document title ----
  const suffix = options.titleSuffix ?? '';
  document.title = `${brand.name}${suffix}`;
}
